import { createContext, useContext } from 'react'
import type { ManualItineraryDeleteRequest, ManualItineraryMoveRequest, TripItineraryData } from 'tripPlanner/api/types'

export interface ItineraryScrollTarget {
  stopId: number
  dayId?: number
  itemId?: number
}

export type CheckoutCartReconciliation =
  | { status: 'reconciled' }
  | { status: 'chat_updating' }
  | { status: 'failed'; error: unknown }

export interface ItineraryContextValue {
  itineraryData: TripItineraryData | undefined
  hasItineraryData: boolean
  /** True only while the first itinerary fetch for a chat is in flight. */
  isItineraryLoading: boolean
  multiCartId: string | undefined
  enrichingStopIds: Set<number>
  enrichingItemIds: Set<number>
  scrollTarget: ItineraryScrollTarget | undefined
  clearScrollTarget: () => void
  refetchCurrentChatItinerary: () => void
  /** `displayName` names the target while the delete is pending. */
  deleteManualItineraryTarget: (target: ManualItineraryDeleteRequest, displayName: string) => Promise<boolean>
  moveManualItineraryItems: (request: ManualItineraryMoveRequest) => Promise<boolean>
  isManualItineraryDeletePending: boolean
  isManualItineraryMovePending: boolean
  isFlightCartMutationPending: boolean
  beginFlightCartMutation: () => () => void
  captureCartOperationScope: () => () => boolean
  reconcileCartForCheckout: (chatId: number, multiCartId: string, tripItemIds: Array<number>) => Promise<CheckoutCartReconciliation>
}

export const ItineraryContext = createContext<ItineraryContextValue | undefined>(undefined)

export function useItineraryContext(): ItineraryContextValue {
  const ctx = useContext(ItineraryContext)
  if (!ctx) throw new Error('useItineraryContext must be used within TripPlannerChatProvider')
  return ctx
}

/**
 * Shared add-to-trip UI (offer-page assistant, hover cards) reads this optionally.
 * Only TripPlannerChatProvider supplies a value; other surfaces leave it undefined.
 */
export function useItineraryContextOptional(): ItineraryContextValue | undefined {
  return useContext(ItineraryContext)
}
